import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Select,
  Checkbox,
  Radio,
  Row,
  Col,
  DatePicker,
} from "antd";
import moment from "moment";

import {
  AGE_RANGE,
  CHILDREN_ACTIVITY_TYPE,
} from "../../../../constants/constant";
import {
  AGE_GROUPS,
  ACTIVITY_TYPE,
  COST_TYPE,
  ANALYTICS_SECTIONS,
} from "../../../../constants/constantTypes";
import {
  ActivityTagLookup,
  ChildrenActivityLookup,
  AccommodationLookup,
  RecommendConnectLookup,
  ActivityTypeLookup,
} from "../../../../containers/Lookups";
import CategoryLookup from "../../../../containers/Lookups/Category";
import AccessCategory from "../../../../containers/Lookups/CategoryLookup/Category";
import ChildActivitySelect from "../../../../containers/Lookups/Activity/ChildActivitySelect";
import AddActivityLookup from "../../../../containers/Lookups/Activity/AddActivityLookup";
import useDebounce from "../../../../custom-hooks/useDebounce";
import dateTimeService from "../../../../util/date-time.service";

import "./index.less";

const DEEP_LINK_MODULES = {
  SEARCH: "search",
  HOME: "home",
  EXPLORE: "explore",
  FREEBIES: "freebie",
  ACCESS: "access",
  RECOMMEND_CONNECT: "recommend-connect",
  EDUCATION_SUPPORT: "education-support",
  CHAT: "chat",
  EVENTS: "events",
  EVENT_SEARCH: "event",
  ACTIVITIES: "children-activity",
};

export const ExploreConstants = {
  Chat: 4, // post or chat
  RecommendAndConnect: 2, // community and resource
  Freebie: 6, //freebies
  ChildCareEducation: 13, // childcare and education
  Event: 11, // events
  Activities: 12, //ChildrenActivity: 11,
  Materials: 5, //  backend call this Access : 4,
  HelpMeGrow: 14,
  Home: 20,
  Explore: 1,
};

// "Global Search" categories-as-modules multi-select.
const DEEP_LINK_MODULE_OPTIONS = [
  {
    value: DEEP_LINK_MODULES.SEARCH,
    label: "Global Search",
  },
  {
    value: DEEP_LINK_MODULES.HOME,
    label: "Home",
  },
  {
    value: DEEP_LINK_MODULES.EXPLORE,
    label: "Explore",
  },
  {
    value: DEEP_LINK_MODULES.FREEBIES,
    label: "Freebies",
  },
  {
    value: DEEP_LINK_MODULES.ACCESS,
    label: "Materials",
  },
  {
    value: DEEP_LINK_MODULES.RECOMMEND_CONNECT,
    label: "Community Resources",
  },
  {
    value: DEEP_LINK_MODULES.EDUCATION_SUPPORT,
    label: "Childcare & Education",
  },
  {
    value: DEEP_LINK_MODULES.ACTIVITIES,
    label: "Activities",
  },
  // {
  //   value: DEEP_LINK_MODULES.CHAT,
  //   label: "Chat",
  // },
  {
    value: DEEP_LINK_MODULES.EVENTS,
    label: "Events",
  },
];

const DEEP_LINK_CATEGORY_OPTIONS = [
  ...DEEP_LINK_MODULE_OPTIONS.filter(
    (opt) =>
      opt.value !== DEEP_LINK_MODULES.SEARCH &&
      opt.value !== DEEP_LINK_MODULES.HOME &&
      opt.value !== DEEP_LINK_MODULES.EXPLORE,
  ),
  {
    value: DEEP_LINK_MODULES.CHAT,
    label: "Chat",
  },
];

export const DEEP_LINK_BASE_URL = "https://link.parentpass.app/deeplink";

const DeepLinkModal = ({ visible = false, onCancel, form }) => {
  const [deepLinkForm] = Form.useForm();
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [generatedUrlSpanish, setGeneratedUrlSpanish] = useState("");
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);

  useEffect(() => {
    if (visible) {
      const existingValues = form.getFieldsValue([
        "deepLinkModule",
        "deepLinkQuery",
        "deepLinkCategories",
        "deepLinkAgeGroups",
        "deepLinkSubCategories",
        "deepLinkTags",
        "deepLinkDate",
        "deepLinkCostType",
        "deepLinkAccommodationIds",
        "deepLinkActivityType",
        "deepLinkTopicIds",
        "deepLinkSections",
        "deepLink",
        "deepLinkSpanish",
      ]);
      deepLinkForm.setFieldsValue({
        module: existingValues.deepLinkModule,
        query: existingValues.deepLinkQuery,
        categories: existingValues.deepLinkCategories,
        ageGroups: existingValues.deepLinkAgeGroups,
        subCategories: existingValues.deepLinkSubCategories,
        tagIds: existingValues.deepLinkTags,
        date: existingValues.deepLinkDate,
        costType: existingValues.deepLinkCostType,
        accomodationIds: existingValues.deepLinkAccommodationIds,
        subCategoryIds: existingValues.deepLinkActivityType,
        topicIds: existingValues.deepLinkTopicIds,
        sections: existingValues.deepLinkSections,
      });
      if (existingValues.deepLink) {
        setGeneratedUrl(existingValues.deepLink);
        setGeneratedUrlSpanish(existingValues.deepLinkSpanish || "");
      }
    }
  }, [visible]);

  const buildDeepLinkUrl = (values, isSpanish = false) => {
    const {
      module,
      query,
      categories,
      ageGroups,
      subCategories,
      tagIds,
      tagIdsExplore,
      date,
      costType,
      accomodationIds,
      latitude,
      longitude,
      activityType,
      subCategoryIds,
      topicIds,
      sections,
    } = values;

    let url = `${DEEP_LINK_BASE_URL}`;

    // If no module is selected, default to global search:
    if (!module || module === DEEP_LINK_MODULES.SEARCH) {
      url += `/search`;
    } else if (
      module === DEEP_LINK_MODULES.HOME ||
      module === DEEP_LINK_MODULES.EXPLORE
    ) {
      url += `/${module}`;
    } else {
      url += `/${module}/search`;
    }

    // specific check for Home and Explore to return early if no params needed
    if (module === DEEP_LINK_MODULES.HOME) {
      return url;
    }

    const params = new URLSearchParams();

    const normalizeList = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.map((v) =>
          typeof v === "object" && v !== null
            ? (v.id ?? v.value ?? v.key ?? v)
            : v,
        );
      }
      // single item
      return [
        typeof value === "object" && value !== null
          ? (value.id ?? value.value ?? value.key ?? value)
          : value,
      ];
    };

    // Add query parameter
    if (query) {
      params.append("query", query);
    }

    // the numeric enum values expected by the backend.
    const mapGlobalCategoriesToEnum = (modulesList) => {
      if (!modulesList || modulesList.length === 0) return [];

      return modulesList
        .map((m) => {
          switch (m) {
            case DEEP_LINK_MODULES.EVENTS:
              return ExploreConstants.Event;
            case DEEP_LINK_MODULES.ACTIVITIES:
            case DEEP_LINK_MODULES.CHILDREN_ACTIVITY:
              return ExploreConstants.Activities;
            case DEEP_LINK_MODULES.FREEBIES:
              return ExploreConstants.Freebie;
            case DEEP_LINK_MODULES.ACCESS:
              return ExploreConstants.Materials;
            case DEEP_LINK_MODULES.CHAT:
              return ExploreConstants.Chat;
            case DEEP_LINK_MODULES.RECOMMEND_CONNECT:
              return ExploreConstants.RecommendAndConnect;
            case DEEP_LINK_MODULES.EDUCATION_SUPPORT:
              return ExploreConstants.ChildCareEducation;
            case DEEP_LINK_MODULES.HOME:
              return ExploreConstants.Home;
            case DEEP_LINK_MODULES.EXPLORE:
              return ExploreConstants.Explore;
            default:
              return null;
          }
        })
        .filter((v) => v !== null && v !== undefined);
    };

    // For module-specific search, backend expects these as subCategoryIds (or subCategoryId).
    const normalizedCategories = normalizeList(categories);
    const isGlobalSearch = !module || module === DEEP_LINK_MODULES.SEARCH;
    const isChatModule = module === DEEP_LINK_MODULES.CHAT;
    const isActivitiesModule = module === DEEP_LINK_MODULES.ACTIVITIES;

    const isExploreModule = module === DEEP_LINK_MODULES.EXPLORE;
    const isHome = module === DEEP_LINK_MODULES.HOME;

    // For Global Search and Chat, "categories" must be the numeric enum values
    const finalCategories = isGlobalSearch
      ? mapGlobalCategoriesToEnum(normalizedCategories)
      : normalizedCategories;

    if (finalCategories && finalCategories.length > 0) {
      const categoryParamName = isGlobalSearch
        ? "categories"
        : module === DEEP_LINK_MODULES.FREEBIES ||
            module === DEEP_LINK_MODULES.ACCESS
          ? "subCategoryIds"
          : "categories";

      params.append(categoryParamName, finalCategories.join(","));
    }

    // Add age groups
    const normalizedAgeGroups = normalizeList(ageGroups);
    if (normalizedAgeGroups && normalizedAgeGroups.length > 0) {
      params.append("ageGroups", normalizedAgeGroups.join(","));
    }

    // Add subcategories
    const normalizedSubCategories = normalizeList(subCategories);
    if (normalizedSubCategories && normalizedSubCategories.length > 0) {
      const paramName =
        module === DEEP_LINK_MODULES.FREEBIES ||
        module === DEEP_LINK_MODULES.ACCESS
          ? "subCategoryIds"
          : "subCategoryIds";

      params.append(paramName, normalizedSubCategories.join(","));
    }

    // Add tags
    const normalizedTagIds = normalizeList(tagIds);
    if (normalizedTagIds && normalizedTagIds.length > 0) {
      if (module !== DEEP_LINK_MODULES.EXPLORE) {
        params.append("tagIds", normalizedTagIds.join(","));
      }
    }

    // Add tags
    const normalizedTagIdsExplore = normalizeList(tagIdsExplore);
    if (normalizedTagIdsExplore && normalizedTagIdsExplore.length > 0) {
      if (DEEP_LINK_MODULES.EXPLORE === module) {
        params.append("interest-tag-id", normalizedTagIdsExplore.join(","));
      }
    }

    // Add date (for events module)
    if (date && module === DEEP_LINK_MODULES.EVENTS) {
      params.append("date", moment(date).format("YYYY-MM-DD"));
    }

    // Add cost type (for events module)
    if (
      costType !== undefined &&
      costType !== null &&
      module === DEEP_LINK_MODULES.EVENTS
    ) {
      params.append("costType", costType);
    }

    // Add accommodation IDs
    if (
      accomodationIds &&
      accomodationIds.length > 0 &&
      module === DEEP_LINK_MODULES.EVENTS
    ) {
      // Keep legacy spelling to match existing links
      params.append("accomodationIds", accomodationIds.join(","));
    }

    // Add coordinates (for events module)
    if (latitude && module === DEEP_LINK_MODULES.EVENTS) {
      params.append("latitude", latitude);
    }
    if (longitude && module === DEEP_LINK_MODULES.EVENTS) {
      params.append("longitude", longitude);
    }

    // Add activity type as subCategoryIds (for activities module)
    const normalizedActivityTypes = normalizeList(subCategoryIds);
    if (
      normalizedActivityTypes &&
      normalizedActivityTypes.length > 0 &&
      module === DEEP_LINK_MODULES.ACTIVITIES
    ) {
      params.append("subCategoryIds", normalizedActivityTypes.join(","));
    }

    // Add topic IDs (for activities module)
    const normalizedTopicIds = normalizeList(topicIds);
    if (
      normalizedTopicIds &&
      normalizedTopicIds.length > 0 &&
      module === DEEP_LINK_MODULES.ACTIVITIES
    ) {
      params.append("topicIds", normalizedTopicIds.join(","));
    }

    // Add sections (for recommend-connect module)
    const normalizedSections = normalizeList(sections);
    if (
      normalizedSections &&
      normalizedSections.length > 0 &&
      module === DEEP_LINK_MODULES.RECOMMEND_CONNECT
    ) {
      params.append("subCategoryIds", normalizedSections.join(","));
    }

    // Add sections (for access/materials module)
    if (
      normalizedSections &&
      normalizedSections.length > 0 &&
      module === DEEP_LINK_MODULES.ACCESS
    ) {
      params.append("subCategoryIds", normalizedSections.join(","));
    }

    const queryString = params.toString();
    let finalUrl = queryString ? `${url}?${queryString}` : url;

    finalUrl = finalUrl.replace(/\+/g, "%20");

    return finalUrl;
  };

  const handleFormChange = () => {
    const values = deepLinkForm.getFieldsValue();

    const url = buildDeepLinkUrl(values, false);
    const urlSpanish = buildDeepLinkUrl(values, true);
    setGeneratedUrl(url);
    setGeneratedUrlSpanish(urlSpanish);
  };

  const handleCancel = () => {
    deepLinkForm.resetFields();
    setGeneratedUrl("");
    setGeneratedUrlSpanish("");
    onCancel();
  };

  const handleFinish = (values) => {
    const url = buildDeepLinkUrl(values, false);
    const urlSpanish = buildDeepLinkUrl(values, true);

    // Set form values when deep link is configured
    form.setFieldsValue({
      linkEnglish: url,
      linkSpanish: urlSpanish,
      deepLink: url,
      deepLinkSpanish: urlSpanish,
      deepLinkModule: values.module,
      deepLinkQuery: values.query,
      deepLinkCategories: values.categories,
      deepLinkAgeGroups: values.ageGroups,
      deepLinkSubCategories: values.subCategories,
      deepLinkTags: values.tagIds,
      deepLinkDate: values.date,
      deepLinkCostType: values.costType,
      deepLinkAccommodationIds: values.accomodationIds,
      deepLinkActivityType: values.subCategoryIds,
      deepLinkTopicIds: values.topicIds,
      deepLinkSections: values.sections,
      // Uncheck isInAppRedirection to show the Link fields instead of Title/Type
      isInAppRedirection: false,
      externalLink: url,
      externalLinkSpanish: urlSpanish,
      // isDeepLink: true,
    });
    handleCancel();
  };

  return (
    <Modal
      centered
      keyboard={false}
      footer={false}
      width={800}
      onCancel={handleCancel}
      destroyOnClose={true}
      visible={visible}
      className="add-topic-modal"
      title="Add Deep Link"
    >
      <Form
        form={deepLinkForm}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleFormChange}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="module" label="Category">
              <Select
                placeholder="Select Category"
                style={{ width: "100%" }}
                allowClear
              >
                {DEEP_LINK_MODULE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Form.Item noStyle dependencies={["module"]}>
            {({ getFieldValue }) => {
              const module = getFieldValue("module");
              const isEventsModule = module === DEEP_LINK_MODULES.EVENTS;
              const isActivitiesModule =
                module === DEEP_LINK_MODULES.ACTIVITIES;
              const isChatModule = module === DEEP_LINK_MODULES.CHAT;
              const isRecommendConnectModule =
                module === DEEP_LINK_MODULES.RECOMMEND_CONNECT;
              const isGlobalSearch =
                !module || module === DEEP_LINK_MODULES.SEARCH;
              const isHome = module === DEEP_LINK_MODULES.HOME;
              const isExplore = module === DEEP_LINK_MODULES.EXPLORE;

              if (isHome) return null;

              return (
                <div className="gx-px-2">
                  <Row gutter={16}>
                    {/* Hide search field for Chat and Events modules */}
                    {!isEventsModule &&
                      !isChatModule &&
                      !isHome &&
                      !isExplore && (
                        <Col span={24}>
                          <Form.Item name="query" label="Query">
                            <Input placeholder="Enter search query" />
                          </Form.Item>
                        </Col>
                      )}

                    {/* Show categories for Global Search and Chat */}
                    {isGlobalSearch && (
                      <Col span={24}>
                        <Form.Item name="categories" label="Categories">
                          <Select
                            mode="multiple"
                            placeholder="Select Categories"
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                          >
                            {DEEP_LINK_CATEGORY_OPTIONS.map((opt) => (
                              <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    )}

                    {/* Show categories for Freebie module */}
                    {module === DEEP_LINK_MODULES.FREEBIES && (
                      <Col span={24}>
                        <Form.Item name="categories" label="Category">
                          <CategoryLookup
                            mode="multiple"
                            placeholder="Select Category"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {isRecommendConnectModule && (
                      <Col span={24}>
                        <Form.Item
                          label="Sections"
                          name="sections"
                          required={false}
                        >
                          <RecommendConnectLookup
                            mode="multiple"
                            placeholder="Select Sections"
                            style={{ width: "100%" }}
                            iscustomizewidth={false}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {module === DEEP_LINK_MODULES.ACCESS && (
                      <Col span={24}>
                        <Form.Item
                          label="Sections"
                          name="sections"
                          required={false}
                        >
                          <AccessCategory
                            mode="multiple"
                            placeholder="Select Sections"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {!isHome && !isExplore && (
                      <Col span={24}>
                        <Form.Item noStyle dependencies={["ageGroups"]}>
                          {({ getFieldValue: getAgeGroupValue }) => {
                            return (
                              <Form.Item
                                label="Age Range"
                                name="ageGroups"
                                required={false}
                              >
                                <Checkbox.Group className="custom-check-group">
                                  {AGE_RANGE.map((info) => {
                                    return (
                                      <Checkbox
                                        value={info.value}
                                        disabled={
                                          getAgeGroupValue(
                                            "ageGroups",
                                          )?.includes(AGE_GROUPS.ANY) &&
                                          info.value !== AGE_GROUPS.ANY
                                        }
                                        key={info?.value}
                                      >
                                        {info?.label}
                                      </Checkbox>
                                    );
                                  })}
                                </Checkbox.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    )}

                    {isEventsModule && (
                      <>
                        <Col span={12}>
                          <Form.Item name="date" label="Date">
                            <DatePicker
                              format={
                                dateTimeService.DATE_TIME_FORMAT
                                  .US_DATE_FORMAT_SLASH
                              }
                              style={{ width: "100%" }}
                              placeholder="Select Date"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            label="Accommodation Type"
                            name="accomodationIds"
                            required={false}
                          >
                            <AccommodationLookup />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            label="Is This Event Free?"
                            name="costType"
                            required={false}
                            initialValue={COST_TYPE.FREE}
                          >
                            <Radio.Group>
                              <Radio value={COST_TYPE.FREE}>Yes</Radio>
                              <Radio value={COST_TYPE.DISCOUNT}>No</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>

                        <Col span={24}>
                          <Form.Item
                            rules={[]}
                            label="Interest Tags"
                            name="tagIds"
                            required={false}
                          >
                            <ActivityTagLookup style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </>
                    )}

                    {/* activity types */}
                    {isActivitiesModule && (
                      <Col span={24} style={{ width: "100%" }}>
                        <Form.Item
                          rules={[]}
                          label="Activity Type"
                          name="subCategoryIds"
                          required={false}
                          style={{ width: "100%" }}
                        >
                          <ActivityTypeLookup
                            mode="multiple"
                            placeholder="Please select"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {isExplore && (
                      <Col span={24}>
                        <Form.Item
                          rules={[]}
                          label="Interest Tags"
                          name="tagIdsExplore"
                          required={false}
                        >
                          <ActivityTagLookup
                            style={{ width: "100%" }}
                            isSingleSelect={true}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                </div>
              );
            }}
          </Form.Item>

          {generatedUrl && (
            <Col span={24}>
              <Form.Item label="Generated Deep Link">
                <Input.TextArea
                  value={generatedUrl}
                  readOnly
                  rows={2}
                  style={{ fontFamily: "monospace", fontSize: "12px" }}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!generatedUrl}
                >
                  Create Link
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default DeepLinkModal;
